import { motion } from 'framer-motion';
import { Sparkles, TrendingUp, Hash, FileText, Tag, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ScorePillProps {
  label: string;
  score: number;
  delay?: number;
}

function ScorePill({ label, score, delay = 0 }: ScorePillProps) {
  const getColorClass = (s: number) => {
    if (s >= 80) return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
    if (s >= 60) return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
    return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
  };

  return (
    <motion.div
      className={`flex items-center justify-between px-3 py-2 rounded-lg border ${getColorClass(score)}`}
      initial={{ opacity: 0, scale: 0.85 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.35, type: 'spring', stiffness: 300, damping: 20 }}
    >
      <span className="text-xs font-medium">{label}</span>
      <span className="text-sm font-bold">{score}</span>
    </motion.div>
  );
}

const featureItems = [
  {
    icon: TrendingUp,
    iconBg: 'bg-purple-100 dark:bg-purple-900/30',
    iconColor: 'text-purple-600 dark:text-purple-400',
    titleKey: 'landing.ai.f1title',
    descKey: 'landing.ai.f1desc',
  },
  {
    icon: Hash,
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    iconColor: 'text-amber-600 dark:text-amber-400',
    titleKey: 'landing.ai.f2title',
    descKey: 'landing.ai.f2desc',
  },
  {
    icon: AlertCircle,
    iconBg: 'bg-rose-100 dark:bg-rose-900/30',
    iconColor: 'text-rose-600 dark:text-rose-400',
    titleKey: 'landing.ai.f3title',
    descKey: 'landing.ai.f3desc',
  },
  {
    icon: FileText,
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
    titleKey: 'landing.ai.f4title',
    descKey: 'landing.ai.f4desc',
  },
];

const hashtagData = [
  { tag: '#MacBookHacks', score: 92, good: true },
  { tag: '#TechTips', score: 88, good: true },
  { tag: '#Productivity', score: 75, good: true },
  { tag: '#Apple', score: 45, good: false },
  { tag: '#Viral', score: 30, good: false },
];

export function AIIntelligenceSection() {
  const { t } = useTranslation();
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 ">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300 text-sm font-medium mb-6">
            <motion.div
              animate={{ rotate: [0, 20, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
            >
              <Sparkles className="w-4 h-4" />
            </motion.div>
            {t('landing.ai.badge')}
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            {t('landing.ai.headingPrefix')} <span className="text-primary-600 dark:text-primary-400">{t('landing.ai.headingHighlight')}</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            {t('landing.ai.subtitle')}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Feature List */}
          <div className="space-y-8">
            {featureItems.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={t(item.titleKey)}
                  className="flex items-start gap-4"
                  initial={{ opacity: 0, x: -24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                >
                  <motion.div
                    className={`w-12 h-12 rounded-xl ${item.iconBg} flex items-center justify-center flex-shrink-0`}
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 15 }}
                  >
                    <Icon className={`w-6 h-6 ${item.iconColor}`} />
                  </motion.div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t(item.titleKey)}</h3>
                    <p className="text-gray-600 dark:text-gray-400">{t(item.descKey)}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Right: Score Widget */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden">
              {/* Widget Header */}
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-gray-500" />
                  <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">{t('landing.ai.contentScore')}</span>
                </div>
                <motion.div
                  className="px-3 py-1 rounded-full text-lg font-bold border text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ type: 'spring', stiffness: 300, damping: 15, delay: 0.3 }}
                >
                  78 / 100
                </motion.div>
              </div>

              {/* Score Breakdown */}
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <ScorePill label={t('landing.mock.title')} score={85} delay={0.1} />
                  <ScorePill label={t('card.description')} score={72} delay={0.15} />
                  <ScorePill label={t('landing.mock.tags')} score={90} delay={0.2} />
                  <ScorePill label="SEO" score={65} delay={0.25} />
                </div>

                {/* Hashtag List */}
                <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('landing.ai.hashtagQuality')}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {hashtagData.map((item, i) => (
                      <motion.span
                        key={item.tag}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border ${
                          item.good
                            ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 border-green-200 dark:border-green-800'
                            : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 border-red-200 dark:border-red-800'
                        }`}
                        initial={{ opacity: 0, scale: 0.75 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.35 + i * 0.07, type: 'spring', stiffness: 300, damping: 18 }}
                        whileHover={{ scale: 1.05 }}
                      >
                        {item.good ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        {item.tag}
                        <span className="font-bold">{item.score}</span>
                      </motion.span>
                    ))}
                  </div>
                </div>

                {/* Suggestions */}
                <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
                  <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">{t('landing.ai.improvements')}</p>
                  <ul className="space-y-2">
                    {[
                      'Title is good but could be more specific',
                      'Add a call-to-action in the description',
                      'Remove #Apple and #Viral — they hurt reach',
                    ].map((suggestion, i) => (
                      <motion.li
                        key={i}
                        className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400"
                        initial={{ opacity: 0, x: 10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.55 + i * 0.08, duration: 0.35 }}
                      >
                        <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                        {suggestion}
                      </motion.li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Decorative blobs */}
            <div className="absolute -top-4 -right-4 w-24 h-24 bg-purple-200/50 dark:bg-purple-800/20 rounded-full blur-2xl pointer-events-none" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-blue-200/50 dark:bg-blue-800/20 rounded-full blur-2xl pointer-events-none" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
